import axios from '../services/axios';
import swal from './swal';
import { encerrarChatSuporte, garantirDonoDoChat, removerWidgetSuporte } from './chatSuporteSessao';
import { decode as base64_decode } from 'base-64';
import { ehDemonstracao } from './planoConta';

/**
 * Chat de atendimento, sob demanda.
 *
 * O balão não fica na tela: o chat é carregado e aberto quando o cliente clica
 * em "Chat ao vivo", no menu de ajuda. Ao fechar a janela, o widget é removido
 * por inteiro — para conversar de novo, o cliente passa pelo menu outra vez.
 *
 * O backend garante que a conta e o usuário existam no TomTicket e devolve um
 * token de Single Sign-On junto com os parâmetros do widget, para o chat já
 * saber com quem está falando.
 */

const ID_TAG = 'tomticket-chat-script';

/** Evita montar duas vezes em cliques seguidos. */
let carregando = false;

/**
 * Token já buscado, à espera do clique.
 *
 * A requisição leva cerca de um segundo em desenvolvimento, quase tudo gasto
 * abrindo conexão com o banco. Buscar quando o menu abre esconde essa espera
 * atrás do tempo que a pessoa leva para mover o mouse até o item.
 */
let preparado = null;

/**
 * Busca em andamento, para o clique aguardar a mesma em vez de abrir outra.
 *
 * Sem isto, clicar antes de o preparo terminar dispara uma segunda requisição:
 * as duas provisionam ao mesmo tempo e a conta acaba com duas organizações no
 * TomTicket.
 */
let buscaEmCurso = null;

/** Margem confortável diante do token, que vale uma hora. */
const VALIDADE_DO_PREPARO = 10 * 60 * 1000;

const preparoUtil = () =>
  preparado && preparado.dono === donoAtual() && Date.now() - preparado.em < VALIDADE_DO_PREPARO
    ? preparado
    : null;

/**
 * Busca o token e os parâmetros do widget.
 *
 * @param {boolean} silencioso não alerta em caso de falha
 */
const buscarAcesso = async (silencioso) => {
  try {
    const res = await axios.post('/api/tickets/', { dest: 'chat' });
    const { jwt, script } = res?.data || {};

    if (!jwt || !script?.url) {
      if (!silencioso) mostrarFalha(res?.data?.detail, res?.data?.error);
      return null;
    }

    return { jwt, script, dono: donoAtual(), em: Date.now() };
  } catch (err) {
    if (!silencioso) mostrarFalha(err?.detail || err?.details, err?.message || err?.code);
    return null;
  }
};

/**
 * Adianta a busca do token. Chamado quando o menu de ajuda abre; falha em
 * silêncio, porque ninguém pediu o chat ainda.
 */
export const prepararChatSuporte = async () => {
  if (ehDemonstracao()) return;
  if (widgetPronto() || carregando || preparoUtil() || buscaEmCurso) return;

  buscaEmCurso = buscarAcesso(true).finally(() => {
    buscaEmCurso = null;
  });

  preparado = await buscaEmCurso;
};

/** O widget expõe `comTTChat` globalmente depois de carregar. */
const widget = () => (typeof window !== 'undefined' ? window.comTTChat : undefined);

const widgetPronto = () =>
  Boolean(widget()?.core?.openContainer && document.getElementById('comTomTicketChatWidget'));

/**
 * Identificador do vínculo conta+usuário desta aba, no mesmo formato que o
 * backend usa como customer_id. Serve para saber de quem é a conversa guardada.
 */
const donoAtual = () => {
  try {
    const bruto = sessionStorage.getItem('ddesp');
    if (!bruto) return '';

    const users = JSON.parse(base64_decode(bruto));
    const emp = users?.[0];
    const uid = users?.[3];

    return emp && uid ? `${emp}-${uid}` : '';
  } catch {
    return '';
  }
};

/**
 * O loader do TomTicket lê esta variável para saber qual versão carregar. O
 * script gerado no painel a define antes de inserir a tag; sem ela, o loader
 * pode cair no comportamento antigo, que usa document.write.
 */
const marcarVersaoDoLoader = () => {
  try {
    document.tomticketChatLoaderScriptVersion = 2;
  } catch {
    /* não deve falhar, mas não vale derrubar o chat por isso */
  }
};

const montarUrl = (script, jwt) => {
  const params = new URLSearchParams({
    id: script.id,
    account: script.account,
    jwt,
    autoOpen: '1',
    ts: String(Date.now()),
    ref: document.URL,
  });

  if (script.d) params.set('d', script.d);

  return `${script.url}?${params.toString()}`;
};

/* ─── encerrar ao fechar a janela ────────────────────────────────────────── */

const balao = () => document.getElementById('comTomTicketChatWidget');

/**
 * Faz o fechamento da janela remover o widget inteiro.
 *
 * Por padrão o `closeContainer` do TomTicket apenas esconde a janela e devolve
 * o balão à tela; aqui o balão não deve ficar. Duas garantias, porque o botão
 * de fechar vive dentro de um iframe: envolvemos a função que ele chama e, se
 * por algum motivo ela não passar por aqui, um observador percebe o balão
 * reaparecendo — que é justamente o que acontece ao fechar.
 *
 * Remove o widget, mas NÃO apaga as chaves da conversa: elas guardam o
 * identificador do atendimento, e sem elas cada reabertura começaria um novo.
 */
const encerrarAoFechar = () => {
  const core = widget()?.core;
  if (!core || core.__encerraAoFechar) return;

  const original = core.closeContainer;

  core.closeContainer = function (...args) {
    let r;
    try {
      r = original.apply(this, args);
    } finally {
      removerWidgetSuporte();
    }
    return r;
  };

  core.__encerraAoFechar = true;

  const alvo = balao();
  if (!alvo || typeof MutationObserver === 'undefined') return;

  // O widget esconde o balão ao abrir a janela; vê-lo visível de novo significa
  // que a conversa foi fechada.
  const observer = new MutationObserver(() => {
    const el = balao();
    if (!el) {
      observer.disconnect();
      return;
    }
    if (el.style.display !== 'none') {
      observer.disconnect();
      removerWidgetSuporte();
    }
  });

  observer.observe(alvo, { attributes: true, attributeFilter: ['style', 'class'] });
};

/**
 * O script carrega de forma assíncrona, então o widget só existe algum tempo
 * depois. Fica observando até aparecer, com um teto para não vigiar o DOM
 * indefinidamente caso a carga falhe.
 */
const aoCarregar = (fn, aoDesistir) => {
  if (widgetPronto()) {
    fn();
    return;
  }

  const limite = Date.now() + 20000;
  const timer = setInterval(() => {
    if (widgetPronto()) {
      clearInterval(timer);
      fn();
    } else if (Date.now() > limite) {
      clearInterval(timer);
      if (aoDesistir) aoDesistir();
    }
  }, 300);
};

/* ─── aviso de carregamento ──────────────────────────────────────────────── */

/**
 * Mostra "abrindo" só se a espera passar de um instante.
 *
 * Com o token adiantado pelo menu, a abertura costuma ser imediata — um alerta
 * que aparece e some em seguida incomoda mais do que ajuda. O atraso também
 * cobre o segundo propósito do aviso: impedir cliques repetidos enquanto nada
 * parece estar acontecendo.
 */
const avisoDeCarregamento = () => {
  let aberto = false;

  const timer = setTimeout(() => {
    aberto = true;
    swal.fire({
      title: 'Abrindo o atendimento...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => swal.showLoading(),
    });
  }, 350);

  return () => {
    clearTimeout(timer);
    // Só fecha o que este aviso abriu: outro alerta pode estar na tela.
    if (aberto && swal.isVisible()) swal.close();
    aberto = false;
  };
};

/**
 * Abre o chat. Usado pelo item "Chat ao vivo" do menu de ajuda.
 */
export const abrirChatSuporte = async () => {
  // Conta em demonstração é atendida pelo chat do comercial; não deve sequer
  // ser cadastrada no TomTicket. A guarda fica aqui, e não só no menu, porque
  // é o que garante que nenhum caminho provisione uma conta de 7 dias.
  if (ehDemonstracao()) return false;

  if (widgetPronto()) {
    widget().core.openContainer();
    return true;
  }

  if (carregando) return false;
  carregando = true;

  const fecharAviso = avisoDeCarregamento();

  try {
    // Com o menu aberto antes do clique, o token já está aqui e a abertura é
    // imediata; senão, busca agora.
    const acesso = preparoUtil() || (await (buscaEmCurso || buscarAcesso(false)));
    preparado = null;

    if (!acesso) {
      fecharAviso();
      return false;
    }

    const { jwt, script } = acesso;

    // Se a conversa guardada no navegador for de outro usuário, descarta. O
    // localStorage é comum a todas as abas, então sem isto dois logins
    // diferentes no mesmo navegador compartilhariam a conversa.
    garantirDonoDoChat(donoAtual());

    document.querySelectorAll(`#${ID_TAG}`).forEach((tag) => tag.remove());
    marcarVersaoDoLoader();

    // Construído pelo DOM, como faz o próprio script gerado no painel. A versão
    // antiga do loader usava document.write, que só funciona enquanto a página
    // carrega — aqui o clique vem muito depois, e ali apagaria a página.
    const tag = document.createElement('script');
    tag.id = ID_TAG;
    tag.type = 'text/javascript';
    tag.async = true;
    tag.src = montarUrl(script, jwt);
    tag.onerror = () => {
      fecharAviso();
      // Falha de rede ao buscar o script não é motivo para descartar a
      // conversa: a próxima tentativa deve retomá-la.
      removerWidgetSuporte();
      mostrarFalha('Não foi possível carregar o atendimento.', 'falha_no_script');
    };

    document.body.appendChild(tag);

    aoCarregar(
      () => {
        fecharAviso();
        encerrarAoFechar();
      },
      () => {
        // Passou do tempo e o widget não apareceu: não deixa o aviso preso.
        fecharAviso();
        mostrarFalha('O atendimento não respondeu a tempo.', 'tempo_esgotado');
      },
    );

    return true;
  } catch (err) {
    fecharAviso();
    mostrarFalha(err?.detail || err?.details, err?.message || err?.code);
    return false;
  } finally {
    carregando = false;
  }
};

/**
 * Alerta de falha. O detalhe entra como linha secundária em vez de ser
 * descartado: sem ele a tela mostra sempre a mesma mensagem genérica do
 * interceptor do axios, que não diz o que de fato aconteceu.
 */
const mostrarFalha = (detalhe, causa) => {
  const extra = [detalhe, causa].filter(Boolean).join(' — ');

  swal.fire({
    title: 'Não foi possível abrir o chat',
    html:
      'Tente novamente em instantes.' +
      (extra ? `<br/><small style="color:#90a4ae">${String(extra).slice(0, 300)}</small>` : ''),
    icon: 'error',
    confirmButtonText: 'Fechar',
  });
};

export { encerrarChatSuporte };
export default abrirChatSuporte;
